import { callGemini, fetchServerKeys } from './api';
import { setPendingAfterSettings } from './edit';
import { getFormattedTranscript, triggerDownload } from './exports';
import { getGeminiKey, state } from './state';
import { $, showToast } from './ui';

export type AiActionId = 'summary' | 'minutes' | 'custom';

const PROMPTS: Record<Exclude<AiActionId, 'custom'>, { title: string; instruction: string }> = {
  summary: {
    title: 'Resumen + bullets',
    instruction: `Eres un asistente que resume reuniones y conversaciones.
A partir de la siguiente transcripción, produce:
1. Un resumen ejecutivo de 4-6 frases.
2. Una lista de bullets con los puntos clave (máx. 10).
3. Si procede, una lista breve de próximos pasos.

Formato de salida en Markdown limpio, con encabezados ## para cada sección.`,
  },
  minutes: {
    title: 'Acta de reunión',
    instruction: `Eres un asistente que redacta actas de reunión profesionales.
A partir de la siguiente transcripción, redacta un acta en Markdown con estas secciones (en este orden):
## Asistentes
## Temas tratados
## Decisiones
## Acciones (responsable y fecha si se mencionan)
## Fechas y próximas reuniones

Si una sección no tiene contenido, escribe "—" debajo del encabezado. No inventes datos que no estén en la transcripción.`,
  },
};

function buildPrompt(actionId: AiActionId, customInstruction: string, transcript: string): string {
  const header = actionId === 'custom' ? customInstruction.trim() : PROMPTS[actionId].instruction;
  return `${header}

Transcripción:
"""
${transcript}
"""`;
}

export async function callGeminiOnTranscript(
  apiKey: string,
  actionId: AiActionId,
  customInstruction: string,
  transcript: string
): Promise<string> {
  const prompt = buildPrompt(actionId, customInstruction, transcript);
  return callGemini(apiKey, prompt);
}

async function resolveGeminiKey(retry: () => void): Promise<string | null> {
  let key = getGeminiKey();
  if (key) return key;
  const serverKeys = await fetchServerKeys();
  if (serverKeys?.gemini) {
    key = serverKeys.gemini;
    return key;
  }
  setPendingAfterSettings(retry);
  $('settingsModal').classList.add('active');
  return null;
}

function renderMarkdown(md: string): string {
  const esc = (s: string): string =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const lines = md.split('\n');
  const out: string[] = [];
  let inList = false;
  const closeList = (): void => {
    if (inList) {
      out.push('</ul>');
      inList = false;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^###\s+/.test(line)) {
      closeList();
      out.push(
        `<h3 class="font-headline-md text-[16px] text-on-surface mt-4 mb-2">${esc(line.replace(/^###\s+/, ''))}</h3>`
      );
    } else if (/^##\s+/.test(line)) {
      closeList();
      out.push(
        `<h2 class="font-headline-md text-[18px] text-primary mt-5 mb-2">${esc(line.replace(/^##\s+/, ''))}</h2>`
      );
    } else if (/^#\s+/.test(line)) {
      closeList();
      out.push(
        `<h1 class="font-headline-md text-[20px] text-primary mt-5 mb-2">${esc(line.replace(/^#\s+/, ''))}</h1>`
      );
    } else if (/^[-*]\s+/.test(line)) {
      if (!inList) {
        out.push('<ul class="list-disc pl-6 space-y-1 my-2">');
        inList = true;
      }
      out.push(`<li>${formatInline(esc(line.replace(/^[-*]\s+/, '')))}</li>`);
    } else if (line.trim() === '') {
      closeList();
      out.push('');
    } else {
      closeList();
      out.push(`<p class="my-2 leading-relaxed">${formatInline(esc(line))}</p>`);
    }
  }
  closeList();
  return out.join('\n');
}

function formatInline(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*]+)\*([^*]|$)/g, '$1<em>$2</em>$3')
    .replace(/`([^`]+)`/g, '<code class="bg-surface-container px-1 rounded">$1</code>');
}

interface AiAction {
  id: AiActionId;
  label: string;
  filename: string;
  needsCustom?: boolean;
}

const ACTIONS: AiAction[] = [
  { id: 'summary', label: 'Resumen + bullets', filename: 'Resumen' },
  { id: 'minutes', label: 'Acta de reunión', filename: 'Acta' },
  { id: 'custom', label: 'Prompt libre…', filename: 'IA', needsCustom: true },
];

export async function runAiAction(actionId: AiActionId, customInstruction = ''): Promise<void> {
  if (state.paragraphs.length === 0) {
    showToast('No hay transcripción todavía.', 'warning');
    return;
  }

  const action = ACTIONS.find((a) => a.id === actionId);
  if (!action) return;

  if (action.needsCustom && !customInstruction.trim()) {
    showToast('Escribe una instrucción para la IA.', 'warning');
    return;
  }

  const key = await resolveGeminiKey(() => void runAiAction(actionId, customInstruction));
  if (!key) return;

  const modal = $('aiActionModal');
  const titleEl = $('aiActionTitle');
  const bodyEl = $('aiActionBody');
  const loadingEl = $('aiActionLoading');
  const actionsRow = $('aiActionFooterActions');

  titleEl.innerText = action.label;
  bodyEl.innerHTML = '';
  bodyEl.style.display = 'none';
  loadingEl.style.display = 'flex';
  actionsRow.style.display = 'none';
  modal.classList.add('active');

  try {
    const transcript = getFormattedTranscript(false);
    const text = await callGeminiOnTranscript(key, actionId, customInstruction, transcript);
    const cleaned = text.replace(/^\s*```(?:markdown|md)?\s*/i, '').replace(/```\s*$/, '');
    bodyEl.innerHTML = renderMarkdown(cleaned);
    bodyEl.dataset.rawMarkdown = cleaned;
    bodyEl.style.display = 'block';
    loadingEl.style.display = 'none';
    actionsRow.style.display = 'flex';
  } catch (err) {
    console.error(err);
    showToast(`Error Gemini: ${(err as Error).message}`, 'error');
    modal.classList.remove('active');
  }
}

export function setupAiActions(): void {
  const triggerBtn = $<HTMLButtonElement>('aiActionsBtn');
  const menu = $('aiActionsMenu');

  for (const action of ACTIONS) {
    const item = document.createElement('button');
    item.type = 'button';
    item.dataset.action = action.id;
    item.className =
      'w-full text-left px-4 py-2 text-sm text-on-surface hover:bg-primary/5 transition-all flex items-center gap-2';
    item.innerHTML = `<span class="material-symbols-outlined text-sm">${
      action.id === 'summary' ? 'summarize' : action.id === 'minutes' ? 'description' : 'edit_note'
    }</span> ${action.label}`;
    item.addEventListener('click', () => {
      menu.classList.add('hidden');
      if (action.needsCustom) {
        openCustomPromptModal();
      } else {
        void runAiAction(action.id);
      }
    });
    menu.appendChild(item);
  }

  triggerBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    menu.classList.toggle('hidden');
  });

  document.addEventListener('click', (e) => {
    if (menu.classList.contains('hidden')) return;
    const target = e.target as HTMLElement;
    if (!menu.contains(target) && target !== triggerBtn) {
      menu.classList.add('hidden');
    }
  });

  const closeBtn = $('aiActionModalClose');
  closeBtn.addEventListener('click', () => {
    $('aiActionModal').classList.remove('active');
  });

  const copyBtn = $('aiActionCopyBtn');
  copyBtn.addEventListener('click', () => {
    const bodyEl = $('aiActionBody');
    const md = bodyEl.dataset.rawMarkdown ?? '';
    navigator.clipboard
      .writeText(md)
      .then(() => showToast('Copiado al portapapeles.', 'success'))
      .catch(() => showToast('No se pudo copiar.', 'error'));
  });

  const downloadBtn = $('aiActionDownloadBtn');
  downloadBtn.addEventListener('click', () => {
    const bodyEl = $('aiActionBody');
    const md = bodyEl.dataset.rawMarkdown ?? '';
    const titleEl = $('aiActionTitle');
    const filenameBase = ACTIONS.find((a) => a.label === titleEl.innerText)?.filename ?? 'IA';
    const blob = new Blob([md], { type: 'text/markdown' });
    triggerDownload(blob, `${filenameBase}_Scribe.md`);
  });

  setupCustomPromptModal();
}

function setupCustomPromptModal(): void {
  const modal = $('aiCustomPromptModal');
  const cancelBtn = $('aiCustomPromptCancelBtn');
  const runBtn = $('aiCustomPromptRunBtn');
  const textarea = $<HTMLTextAreaElement>('aiCustomPromptInput');

  cancelBtn.addEventListener('click', () => {
    modal.classList.remove('active');
  });

  runBtn.addEventListener('click', () => {
    const instruction = textarea.value.trim();
    if (!instruction) {
      showToast('Escribe una instrucción primero.', 'warning');
      return;
    }
    modal.classList.remove('active');
    void runAiAction('custom', instruction);
  });
}

function openCustomPromptModal(): void {
  const modal = $('aiCustomPromptModal');
  const textarea = $<HTMLTextAreaElement>('aiCustomPromptInput');
  textarea.value = '';
  modal.classList.add('active');
  setTimeout(() => textarea.focus(), 50);
}
